import { useCallback, useEffect, useState } from 'react';
import {
  useLoadingStore,
  loadingBegin,
  loadingEnd,
  setLoadingProgress,
  runLoading,
} from './loadingStore';

/**
 * Component-facing handle on the global loader.  Most code never needs this —
 * the shared axios client drives loading automatically.  Use it for non-API
 * async (building an export file), or to read the live state.
 */
export function useLoading() {
  const active = useLoadingStore((s) => s.active);
  const blocking = useLoadingStore((s) => s.blocking);
  const progress = useLoadingStore((s) => s.progress);

  const run = useCallback(runLoading, []);
  const begin = useCallback(loadingBegin, []);
  const end = useCallback(loadingEnd, []);
  const setProgress = useCallback(setLoadingProgress, []);

  return { isLoading: active > 0, isBlocking: blocking > 0, progress, run, begin, end, setProgress };
}

/**
 * Flicker guard — returns true only after `active` has stayed true for
 * `delayMs`, so sub-100ms operations never flash a spinner.  Hides instantly
 * when `active` goes false.
 */
export function useDelayedFlag(active: boolean, delayMs = 150): boolean {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) {
      setShow(false);
      return;
    }
    const t = window.setTimeout(() => setShow(true), delayMs);
    return () => window.clearTimeout(t);
  }, [active, delayMs]);
  return show;
}
